// @ts-strict-ignore
import React, { useContext } from 'react';
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Icon,
  IconName,
} from '@usealloy/component-library';
import AggregationSelect from './AggregationSelect';
import type { AggregationOption } from './AggregationSelect';
import {
  FILTER_OPERATOR_OPTIONS,
  BOOLEAN_FILTER_OPTIONS,
} from './constants';
import type { AggregationFilter } from 'flow/store/engine/types';
import type { AggregationFilterOption } from 'flow/store/engine/types/props';
import { AggregationFilterOperatorType } from 'flow/store/engine/constants';
import {
  StyledFilterCriteriaRow,
  StyledAggregationClause,
  StyledFilterValueInput,
  StyledAggregationNumberInput,
} from './styles';
import { RuleBuilderContext } from '../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/context';
import { AggregationEditorContext } from './context';

type AggregationFilterRowProps = {
  filter: AggregationFilter;
  filterOption?: AggregationFilterOption;
  onUpdate: (columnSlug: string, updates: Partial<AggregationFilter>) => void;
  onRemove: (columnSlug: string) => void;
};

type FilterValueInputProps = Pick<AggregationFilterRowProps,
'filter' | 'filterOption' | 'onUpdate'>;

/** Default staticValue for a given operator — resets on operator switch. */
const defaultStaticValueForOperator = (
  operator: AggregationFilterOperatorType,
): AggregationFilter['staticValue'] => {
  switch (operator) {
    case AggregationFilterOperatorType.BT: return ['', ''];
    case AggregationFilterOperatorType.IN: return [];
    default: return '';
  }
};

// -- Sub-components ---------------------------------------------------------

/** Single value input whose variant is driven by ui_data_type. */
const FilterDataTypeInput = ({
  value,
  filterOption,
  onChange,
}: {
  value: string;
  filterOption?: AggregationFilterOption;
  onChange: (val: string) => void;
}) => {
  const { multiSelectProps } = useContext(AggregationEditorContext);
  const uiDataType = filterOption?.ui_data_type ?? 'string';

  switch (uiDataType) {
    case 'boolean':
      return (
        <AggregationSelect
          multiSelectProps={multiSelectProps}
          options={BOOLEAN_FILTER_OPTIONS}
          value={value}
          onChange={(option: AggregationOption) => onChange(option.value)}
        />
      );

    case 'integer':
    case 'decimal':
      return (
        <StyledAggregationNumberInput
          minValue={0}
          maxValue={Number.MAX_SAFE_INTEGER}
          decimalScale={uiDataType === 'decimal' ? 2 : undefined}
          value={Number(value) || 0}
          onHandleValueChange={(val: number) => onChange(String(val))}
        />
      );

    // TODO: 'select' needs possible_values from the column config
    // TODO: 'published_attribute' may need special handling
    case 'string':
    case 'select':
    case 'published_attribute':
    default:
      return (
        <StyledFilterValueInput
          type="text"
          value={value}
          placeholder="[enter value]"
          onChange={e => onChange(e.target.value)}
        />
      );
  }
};

/** Renders value input(s) based on the operator type. */
const FilterValueInput = ({
  filter,
  filterOption,
  onUpdate,
}: FilterValueInputProps) => {
  const { containerType } = useContext(RuleBuilderContext);

  const value = filter.staticValue;

  switch (filter.operatorSlug) {
    case AggregationFilterOperatorType.BT: {
      // Defaults guard against malformed payloads where the array may be shorter than 2 elements
      const [lower = '', upper = ''] = value as string[];
      return (
        <>
          <FilterDataTypeInput
            value={lower}
            filterOption={filterOption}
            onChange={val => onUpdate(filter.columnSlug, { staticValue: [val, upper] })}
          />
          <StyledAggregationClause containerType={containerType}>
            and
          </StyledAggregationClause>
          <FilterDataTypeInput
            value={upper}
            filterOption={filterOption}
            onChange={val => onUpdate(filter.columnSlug, { staticValue: [lower, val] })}
          />
        </>
      );
    }

    case AggregationFilterOperatorType.IN:
      return (
        <StyledFilterValueInput
          type="text"
          value={(value as string[]).join(', ')}
          placeholder="[comma-separated values]"
          onChange={e => {
            const values = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
            onUpdate(filter.columnSlug, { staticValue: values });
          }}
        />
      );

    default:
      return (
        <FilterDataTypeInput
          value={value as string}
          filterOption={filterOption}
          onChange={val => onUpdate(filter.columnSlug, { staticValue: val })}
        />
      );
  }
};

// -- Main component ---------------------------------------------------------

const AggregationFilterRow = ({
  filter,
  filterOption,
  onUpdate,
  onRemove,
}: AggregationFilterRowProps) => {
  const { containerType } = useContext(RuleBuilderContext);
  const { multiSelectProps } = useContext(AggregationEditorContext);

  const label = filterOption?.label ?? filter.columnSlug;

  return (
    <StyledFilterCriteriaRow>
      <StyledAggregationClause containerType={containerType}>
        {label}
      </StyledAggregationClause>
      <AggregationSelect
        multiSelectProps={multiSelectProps}
        options={FILTER_OPERATOR_OPTIONS}
        value={filter.operatorSlug}
        onChange={(option: AggregationOption) => {
          const nextOperator = option.value as AggregationFilterOperatorType;
          onUpdate(filter.columnSlug, {
            operatorSlug: nextOperator,
            staticValue: defaultStaticValueForOperator(nextOperator),
          });
        }}
      />
      <FilterValueInput
        filter={filter}
        filterOption={filterOption}
        onUpdate={onUpdate}
      />
      <Button
        size={ButtonSize.xsmall}
        variant={ButtonVariant.tertiary}
        onClick={() => onRemove(filter.columnSlug)}
        aria-label={`Remove ${label} filter`}
      >
        <Icon icon={IconName.Trash} size="small" />
      </Button>
    </StyledFilterCriteriaRow>
  );
};

export default AggregationFilterRow;
